import axios from "./config";
import { AxiosResponse } from "axios";

import {
  DefaultDiscountPayload,
  GetAddPopupAPIResponse,
  GetDefaultDiscountApiResponse,
  PostAddPopupAPIResponse,
  PostDefaultDiscountApiResponse,
  PostPromotionApiResponse,
  PostPromotionGroupApiResponse,
  PostAddPopupAPIpayload,
  PromotionLogsApiResponse,
  PromotionsByIdAPIResponse,
  PromotionsGroupAPIResponse,
  PromotionsGroupByIdAPIResponse,
  GetPromotionsByPromotionGroupIdApiRequest,
  PromotionsGroupPayload,
  PromotionsListAPIResponse,
  PromotionsPayload,
  PromotionStatusApp,
} from "app/types/promotions";
import { ReportApiResponse } from "app/types";

type GetPromptionByIdApiRequest = {
  storeId: number;
  promotionId: number;
};

type PostPromotionsApiRequest = {
  payload: PromotionsPayload;
  storeId: number;
};

type PutPromotionsApiRequest = {
  payload: PromotionsPayload;
  storeId: number;
  promotionId: number;
};

type GetDefaultDiscountRequestParams = {
  storeId: number;
  promotionId: number;
  query: string;
};

type PutDefaultDiscountRequestParams = {
  storeId: number;
  promotionId: number;
  defaultDiscountId: number;
  payload: DefaultDiscountPayload;
};

type DeleteDefaultDiscountRequestParams = {
  storeId: number;
  promotionId: number;
  defaultDiscountId: number;
};

type PostPromotionsGroupApiRequest = {
  payload: PromotionsGroupPayload;
  storeId: number;
};

type PutPromotionsGroupApiRequest = {
  payload: PromotionsGroupPayload;
  storeId: number;
  promotionGroupId: number;
};

export const getPromotionsListApi = (
  storeId: number,
  query = "",
): Promise<AxiosResponse<PromotionsListAPIResponse>> =>
  axios.get<PromotionsListAPIResponse>(`/stores/${storeId}/promotions${query}`);

export const getPromotionsByIdApi = ({
  storeId,
  promotionId,
}: GetPromptionByIdApiRequest): Promise<
  AxiosResponse<PromotionsByIdAPIResponse>
> =>
  axios.get<PromotionsByIdAPIResponse>(
    `/stores/${storeId}/promotions/${promotionId}`,
  );

export const postPromotionsApi = ({
  payload,
  storeId,
}: PostPromotionsApiRequest): Promise<
  AxiosResponse<PostPromotionApiResponse>
> =>
  axios.post<PostPromotionApiResponse>(
    `/stores/${storeId}/promotions`,
    payload,
  );

export const putPromotionsApi = ({
  payload,
  storeId,
  promotionId,
}: PutPromotionsApiRequest): Promise<AxiosResponse<PostPromotionApiResponse>> =>
  axios.put<PostPromotionApiResponse>(
    `/stores/${storeId}/promotions/${promotionId}`,
    payload,
  );

// Default discount CRUD operations

export const getDefaultDiscountsApi = ({
  storeId,
  promotionId,
  query,
}: GetDefaultDiscountRequestParams): Promise<
  AxiosResponse<GetDefaultDiscountApiResponse>
> =>
  axios.get<GetDefaultDiscountApiResponse>(
    `/stores/${storeId}/promotions/${promotionId}/default-discounts${query}`,
  );

type PostDefaultDiscountRequestParams = {
  storeId: number;
  promotionId: number;
  payload: DefaultDiscountPayload;
};

export const postDefaultDiscountApi = ({
  storeId,
  promotionId,
  payload,
}: PostDefaultDiscountRequestParams): Promise<
  AxiosResponse<PostDefaultDiscountApiResponse>
> =>
  axios.post<PostDefaultDiscountApiResponse>(
    `/stores/${storeId}/promotions/${promotionId}/default-discounts`,
    payload,
  );

export const putDefaultDiscountApi = ({
  storeId,
  promotionId,
  defaultDiscountId,
  payload,
}: PutDefaultDiscountRequestParams): Promise<
  AxiosResponse<PostDefaultDiscountApiResponse>
> =>
  axios.put<PostDefaultDiscountApiResponse>(
    `/stores/${storeId}/promotions/${promotionId}/default-discounts/${defaultDiscountId}`,
    payload,
  );

export const deleteDefaultDiscountApi = ({
  storeId,
  promotionId,
  defaultDiscountId,
}: DeleteDefaultDiscountRequestParams): Promise<AxiosResponse<void>> =>
  axios.delete<void>(
    `/stores/${storeId}/promotions/${promotionId}/default-discounts/${defaultDiscountId}`,
  );

type GetPromotionLogsApiRequest = {
  storeId: number;
  promotionId: number;
  query: string;
};
export const getPromotionLogsApi = ({
  storeId,
  promotionId,
  query,
}: GetPromotionLogsApiRequest): Promise<
  AxiosResponse<PromotionLogsApiResponse>
> =>
  axios.get<PromotionLogsApiResponse>(
    `/stores/${storeId}/promotions/${promotionId}/logs${query}`,
  );

export const downloadPromotionsReportApi = (storeId: number, query: string) =>
  axios.get(`stores/${storeId}/reports/fetch-promotion-data${query}`);

// Group promotion Apis

export const getPromotionsGroupApi = (
  storeId: number,
  query: string,
): Promise<AxiosResponse<PromotionsGroupAPIResponse>> =>
  axios.get<PromotionsGroupAPIResponse>(
    `stores/${storeId}/promotion-groups${query}`,
  );

export const getPromotionsGroupByIdApi = ({
  storeId,
  promotionId,
}: GetPromptionByIdApiRequest): Promise<
  AxiosResponse<PromotionsGroupByIdAPIResponse>
> =>
  axios.get<PromotionsGroupByIdAPIResponse>(
    `/stores/${storeId}/promotion-groups/${promotionId}`,
  );

export const postPromotionsGroupApi = ({
  payload,
  storeId,
}: PostPromotionsGroupApiRequest): Promise<
  AxiosResponse<PostPromotionGroupApiResponse>
> =>
  axios.post<PostPromotionGroupApiResponse>(
    `/stores/${storeId}/promotion-groups`,
    payload,
  );

export const putPromotionsGroupApi = ({
  payload,
  storeId,
  promotionGroupId,
}: PutPromotionsGroupApiRequest): Promise<
  AxiosResponse<PostPromotionGroupApiResponse>
> =>
  axios.put<PostPromotionGroupApiResponse>(
    `/stores/${storeId}/promotion-groups/${promotionGroupId}`,
    payload,
  );

export const getPromotionsByPromotionGroupIdApi = ({
  storeId,
  promotionGroupId,
  query,
}: GetPromotionsByPromotionGroupIdApiRequest): Promise<
  AxiosResponse<PromotionsListAPIResponse>
> =>
  axios.get<PromotionsListAPIResponse>(
    `/stores/${storeId}/promotion-groups/${promotionGroupId}/promotions${query}`,
  );

export const patchPromotionStatusByIdApi = ({
  storeId,
  promotionId,
  payload,
}: {
  storeId: number;
  promotionId: number;
  payload: {
    status: PromotionStatusApp;
  };
}): Promise<AxiosResponse<void>> =>
  axios.patch<void>(
    `/stores/${storeId}/promotions/${promotionId}/status`,
    payload,
  );

export const getGroupPromotionReportAPI = (
  query: string,
  storeId: number,
  groupId: number,
): Promise<AxiosResponse<ReportApiResponse>> =>
  axios.get<ReportApiResponse>(
    `/stores/${storeId}/reports/fetch-groups-promotions-data/${groupId}${query}`,
  );

export const getAddPopupAPI = (
  storeId: number,
  query: string,
): Promise<AxiosResponse<GetAddPopupAPIResponse>> =>
  axios.get<GetAddPopupAPIResponse>(`/stores/${storeId}/popups${query}`);
export const PostAddPopupAPI = (
  storeId: number,
  payload: PostAddPopupAPIpayload,
): Promise<AxiosResponse<PostAddPopupAPIResponse>> =>
  axios.post<PostAddPopupAPIResponse>(`/stores/${storeId}/popups`, payload);

export const PutAddPopupAPI = (
  popupId: number,
  storeId: number,
  payload: PostAddPopupAPIpayload,
): Promise<AxiosResponse<PostAddPopupAPIResponse>> =>
  axios.put<PostAddPopupAPIResponse>(
    `/stores/${storeId}/popups/${popupId}`,
    payload,
  );
